import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DEFAULT_SETTINGS, PRESETS_STORAGE_KEY, TTS_MODELS, TTS_SCHEMES, buildSpeakerStyles, buildSystemPrompt, buildTtsRequest, loadLastTtsSettings, loadTtsPresets, schemeLabel } from '../SpeechGeneration';
import { useTtsPreview, requestPreviewText } from '../../utils/ttsPreview';
import { ModelTagRow } from './modelInfo';
import GlossaryEditor from './GlossaryEditor';
import ReplacementsEditor from './ReplacementsEditor';
import { backdropClose } from '../backdrop';

// Which named voiceover preset the batch should use (remembered across runs).
// Empty string means "last used in the Create Voiceover view".
const BATCH_PRESET_STORAGE_KEY = 'voiceoverBatchPreset';

// Voiceover batch panel: the second pipeline, behind the human-approval gate.
// Two quest-level PREP actions come first: ё-fication (the model restores «ё»
// in the source texts themselves) and glossary collection (doubtful word
// forms, web-verified once for the whole quest, into the editable glossary).
// The batch itself then runs per selected point and voiced field: voice
// split → TTS on the text read through the glossary (Gemini, silence
// watchdog) → stores the audio as the ACTIVE version → tags the point
// '🔊 Voiced' when clean. A field whose take no longer reads the way the text
// reads now (a glossary edit, a one-place mark) is voiced again.
// Points run in parallel server-side, throttled per Gemini key. Same chrome
// as the text batch: scope modal, SSE progress, stop/clear.

const READY_TAG = '🎙️ Ready for voiceover';
const VOICED_TAG = '🔊 Voiced';
// [source field, its voice-over twin]
const FIELD_PAIRS = [
  ['description', 'description_voiceover'],
  ['after_message', 'after_message_voiceover'],
];

const isReady = (l) => (l.tags || []).includes(READY_TAG);
const isVoiced = (l) => (l.tags || []).includes(VOICED_TAG);
const hasAnyText = (l) => FIELD_PAIRS.some(([f]) => ((l[f] || '').trim()));
// Fields whose source text exists but whose _voiceover twin doesn't yet.
const fieldsNoVoText = (l) => FIELD_PAIRS
  .filter(([f, v]) => ((l[f] || '').trim()) && !((l[v] || '').trim()))
  .map(([f]) => f);
const FIELD_SHORT = { description: 'desc', after_message: 'after' };

// A two-turn sample the settings preview is planned on (see samplePayload).
const SAMPLE_TAKE = 'Speaker 1: [warmly] A sample turn of Speaker 1.\nSpeaker 2: A sample turn of Speaker 2.';

// Resolve the raw settings object the batch will voice with. With no preset
// name it's the settings last used in the Create Voiceover view (falling back
// to the AI Studio defaults); with a preset name it's that saved named preset.
function resolveTtsSettings(presetName) {
  let s = DEFAULT_SETTINGS;
  try {
    if (presetName) {
      const presets = loadTtsPresets();
      if (presets && presets[presetName]) s = { ...DEFAULT_SETTINGS, ...presets[presetName] };
    } else {
      s = loadLastTtsSettings();
    }
  } catch (e) { /* defaults stay */ }
  return s;
}

// The same settings, shaped as the TTS payload sent to the server. `scheme`
// and `model` set here override the saved ones for this run (see
// TTS_SCHEMES / TTS_MODELS); the server picks the request shape by the model.
function currentTtsSettings(presetName, scheme, model) {
  const s = resolveTtsSettings(presetName);
  return {
    model: model || s.model,
    scheme: (scheme || s.scheme) === 'whole' ? 'whole' : 'turns',
    styles: buildSpeakerStyles(s),
    temperature: s.temperature,
    voices: { speaker1: s.voice1, speaker2: s.voice2 },
    system_prompt: buildSystemPrompt(s),
    panel: s,   // the raw fields, kept with each take (server ttsPanelSettings)
  };
}

async function papi(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

function fmtEta(ms) {
  if (ms == null) return '…';
  if (ms <= 0) return '0s';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const STATUS_CHIP = {
  queued: { bg: '#9aa0a6', label: 'queued' },
  running: { bg: '#8e44ad', label: 'running' },
  done: { bg: '#188038', label: '✓ voiced' },
  partial: { bg: '#9a6700', label: '◐ partial' },
  skipped: { bg: '#9aa0a6', label: 'skipped' },
  error: { bg: '#c5221f', label: 'error' },
  stopped: { bg: '#5f6368', label: 'stopped' },
  interrupted: { bg: '#5f6368', label: 'interrupted' },
};

const UNFINISHED = ['queued', 'running', 'interrupted', 'error'];

// Per-field outcome shown on finished rows: which field got new audio, which
// kept its existing version, which failed — so one shared ERROR chip never
// hides that half the point actually succeeded.
const FIELD_STATE = {
  voiced: { text: '✓ new', color: '#188038', hint: 'new audio generated and set active' },
  kept: { text: '✓ kept', color: '#188038', hint: 'already had an audio version — left as is' },
  silent: { text: '✗ silent', color: '#c5221f', hint: 'every TTS attempt drifted into silence — no active audio, left unvoiced (attempts are in version history)' },
  // Legacy state from batches run before the retry loop — kept so old records still render.
  truncated: { text: '⚠ cut', color: '#9a6700', hint: 'TTS drifted into silence — audio stored but incomplete' },
  error: { text: '✗ failed', color: '#c5221f', hint: 'this field failed — see the error details' },
};

function FieldsSummary({ fields }) {
  const entries = Object.entries(fields || {});
  if (!entries.length) return null;
  return (
    <span style={{ whiteSpace: 'nowrap', fontSize: 10 }}>
      {entries.map(([f, raw], i) => {
        // A field is either a plain state string (kept/error/legacy) or
        // { state, attempts } for outcomes where the retry count matters.
        const { state, attempts } = typeof raw === 'string' ? { state: raw } : (raw || {});
        const meta = FIELD_STATE[state] || { text: state, color: '#5f6368', hint: state };
        let text = meta.text;
        if (state === 'voiced' && attempts > 1) text += ` (try ${attempts})`;
        if (state === 'silent' && attempts) text += ` ×${attempts}`;
        return (
          <span key={f} title={`${f}: ${meta.hint}`} style={{ color: meta.color }}>
            {i > 0 ? ' | ' : ''}{FIELD_SHORT[f] || f} {text}
          </span>
        );
      })}
    </span>
  );
}

function PointRow({ bp, num }) {
  const chip = STATUS_CHIP[bp.status] || STATUS_CHIP.queued;
  const isRunning = bp.status === 'running';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4,
      background: isRunning ? 'rgba(142,68,173,0.10)' : 'transparent', fontSize: 12,
    }}>
      <span style={{
        background: chip.bg, color: '#fff', borderRadius: 10, padding: '1px 8px',
        fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{chip.label}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{num ? `${num}. ` : ''}{bp.name}</span>
      {!isRunning && <FieldsSummary fields={bp.fields} />}
      {isRunning && bp.stage && (
        <span style={{ color: '#8e44ad', whiteSpace: 'nowrap' }}>{bp.stage}</span>
      )}
      {bp.notes && (
        <span title={bp.notes} style={{ whiteSpace: 'nowrap', cursor: 'help' }}>📝</span>
      )}
      {bp.status === 'error' && bp.errors && bp.errors.length > 0 && (
        <span title={bp.errors.join('; ')} style={{ color: '#c5221f', whiteSpace: 'nowrap', cursor: 'help' }}>⚠</span>
      )}
    </div>
  );
}

// One line per Gemini key: label + liveness mark (from the batch preflight or
// the modal's check). Colour is never the only signal — ✓/✗/⏳ carry it.
function KeysStatus({ keys }) {
  if (!keys || !keys.length) return null;
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: '#5f6368' }}>
      {keys.map((k, i) => (
        <span key={`${k.label}-${i}`} title={k.error || (k.ok ? 'alive' : k.ok === null ? 'checking…' : 'dead')}>
          {k.ok === null ? '⏳' : k.ok ? '✓' : '✗'} {k.label}{k.keyMasked ? ` (${k.keyMasked})` : ''}
        </span>
      ))}
    </div>
  );
}

// Compact progress block shared by the two prep batches (ё-fication and
// glossary collection): bar + per-point rows + an optional footer line.
// '✓ voiced' would lie on a prep row, so 'done' gets a neutral label here.
const PREP_CHIP = { ...STATUS_CHIP, done: { bg: '#188038', label: '✓ done' } };

// The glossary batch has two phases on one bar: collect (per point, fast)
// takes the first GLOSSARY_COLLECT_SHARE, verify (web-checked calls, the
// long part) the rest. The verify call count is known only after collect,
// hence a fixed split rather than one sized by the work.
const GLOSSARY_COLLECT_SHARE = 0.3;

function glossaryProgress(b) {
  const total = b.total || 0;
  const collectFrac = total ? (b.done_count || 0) / total : 0;
  const v = b.verify;
  if (!v || v.chunks_total == null) {
    return {
      pct: Math.round(collectFrac * GLOSSARY_COLLECT_SHARE * 100),
      label: `collect ${b.done_count || 0} / ${total} point(s)`,
    };
  }
  const verifyFrac = v.chunks_total ? v.chunks_done / v.chunks_total : 1;
  return {
    pct: Math.round((GLOSSARY_COLLECT_SHARE + (1 - GLOSSARY_COLLECT_SHARE) * verifyFrac) * 100),
    label: `verify ${v.chunks_done} / ${v.chunks_total} call(s)`,
  };
}

function PrepBatchBlock({ title, batch, running, onStop, onClear, rightInfo, footer, orderByUuid, progress }) {
  if (!batch) return null;
  const done = batch.done_count || 0;
  const total = batch.total || 0;
  const pr = progress ? progress(batch) : null;
  const pct = pr ? pr.pct : (total ? Math.round((done / total) * 100) : 0);
  return (
    <div style={{ border: '1px solid var(--border,#eee)', borderRadius: 6, padding: '6px 8px', marginTop: 6, fontSize: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{title}</span>
        <div style={{ flex: 1, height: 6, background: 'rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: running ? '#8e44ad' : '#188038', transition: 'width .3s' }} />
        </div>
        <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>
          {pr ? pr.label : `${done} / ${total}`}
          {batch.error_count ? ` · ${batch.error_count} failed` : ''}
          {!running && batch.status ? ` · ${batch.status}` : ''}
          {running && batch.eta_ms != null ? ` · ETA ${fmtEta(batch.eta_ms)}${pr && batch.phase === 'collect' ? ' + verify' : ''}` : ''}
        </span>
        {running && <button onClick={onStop} style={{ padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }}>■ Stop</button>}
        {!running && (
          <button
            className="remove-location-button"
            onClick={onClear}
            title="Delete this batch record"
            aria-label={`Delete ${title} record`}
          >×</button>
        )}
      </div>
      <div style={{ maxHeight: 140, overflow: 'auto', marginTop: 4 }}>
        {(batch.points || []).map((bp) => {
          const chip = PREP_CHIP[bp.status] || PREP_CHIP.queued;
          return (
            <div key={bp.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px', fontSize: 11 }}>
              <span style={{ background: chip.bg, color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 9, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{chip.label}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderByUuid && orderByUuid[bp.uuid] ? `${orderByUuid[bp.uuid]}. ` : ''}{bp.name}</span>
              {bp.status === 'running' && bp.stage && <span style={{ color: '#8e44ad', whiteSpace: 'nowrap' }}>{bp.stage}</span>}
              {rightInfo && <span style={{ color: '#5f6368', whiteSpace: 'nowrap' }}>{rightInfo(bp)}</span>}
              {bp.errors && bp.errors.length > 0 && (
                <span title={bp.errors.join('; ')} style={{ color: '#c5221f', whiteSpace: 'nowrap', cursor: 'help' }}>⚠</span>
              )}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}

const VoiceoverBatchPanel = ({ questId, runId, locations, pointsByUuid, initialBatch, initialYoficationBatch, initialGlossaryBatch, embedded, onStatus }) => {
  const [batch, setBatch] = useState(initialBatch || null);
  const [active, setActive] = useState(!!(initialBatch && initialBatch.status === 'running'));
  const [showScope, setShowScope] = useState(false);
  const [selected, setSelected] = useState({}); // uuid -> bool
  const [skipVoiced, setSkipVoiced] = useState(true);
  // Per-run steering set in the scope modal. The preset choice is remembered
  // across runs; the free-form instruction is one-off and cleared on start.
  const [ttsPreset, setTtsPreset] = useState(() => {
    try { return localStorage.getItem(BATCH_PRESET_STORAGE_KEY) || ''; } catch (e) { return ''; }
  });
  const [presetNames, setPresetNames] = useState([]);
  const [voiceSplitExtra, setVoiceSplitExtra] = useState('');
  // '' — as the preset / last settings say; else this run's own scheme / model.
  const [ttsScheme, setTtsScheme] = useState('');
  const [ttsModel, setTtsModel] = useState('');
  const [keyCheck, setKeyCheck] = useState(null); // modal's preflight result
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const clearedRef = useRef(false);

  // Prep actions: ё-fication and glossary collection (their own batches on the
  // server, run-exclusive with everything else), plus the glossary editor.
  const [yoBatch, setYoBatch] = useState(initialYoficationBatch || null);
  const [yoActive, setYoActive] = useState(!!(initialYoficationBatch && initialYoficationBatch.status === 'running'));
  const [glBatch, setGlBatch] = useState(initialGlossaryBatch || null);
  const [glActive, setGlActive] = useState(!!(initialGlossaryBatch && initialGlossaryBatch.status === 'running'));
  const [showGlossary, setShowGlossary] = useState(false);
  const [showReplacements, setShowReplacements] = useState(false);
  const [glossaryCount, setGlossaryCount] = useState(null);
  const [showYoScope, setShowYoScope] = useState(false);
  const [yoSelected, setYoSelected] = useState({}); // uuid -> bool
  const yoClearedRef = useRef(false);
  const glClearedRef = useRef(false);

  useEffect(() => {
    if (initialBatch && !batch && !clearedRef.current) {
      setBatch(initialBatch);
      if (initialBatch.status === 'running') setActive(true);
    }
  }, [initialBatch, batch]);
  useEffect(() => {
    if (initialYoficationBatch && !yoBatch && !yoClearedRef.current) {
      setYoBatch(initialYoficationBatch);
      if (initialYoficationBatch.status === 'running') setYoActive(true);
    }
  }, [initialYoficationBatch, yoBatch]);
  useEffect(() => {
    if (initialGlossaryBatch && !glBatch && !glClearedRef.current) {
      setGlBatch(initialGlossaryBatch);
      if (initialGlossaryBatch.status === 'running') setGlActive(true);
    }
  }, [initialGlossaryBatch, glBatch]);

  const fetchGlossaryCount = () => {
    if (!questId) return;
    fetch(`/api/pipeline/quest/${questId}/glossary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setGlossaryCount((d.glossary || []).length); })
      .catch(() => { /* count stays unknown */ });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchGlossaryCount, [questId]);

  // Remember the chosen voiceover preset across runs.
  useEffect(() => {
    try { localStorage.setItem(BATCH_PRESET_STORAGE_KEY, ttsPreset || ''); } catch (e) { /* ignore */ }
  }, [ttsPreset]);

  // Refresh the saved-preset list whenever the scope modal opens (it's edited
  // in the Create Voiceover view). Drop a remembered choice that no longer exists.
  useEffect(() => {
    if (!showScope) return;
    let names = [];
    try { names = Object.keys(JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}') || {}); } catch (e) { /* none */ }
    setPresetNames(names);
    setTtsPreset((cur) => (cur && !names.includes(cur) ? '' : cur));
  }, [showScope]);

  // What the chosen preset actually sends to TTS — shown as a preview so a
  // preset name like «natural» isn't opaque. Recomputed when the modal opens
  // (settings may have changed in the Create Voiceover view) or the pick changes.
  const ttsPreview = useMemo(() => {
    const s = resolveTtsSettings(ttsPreset);
    return {
      model: ttsModel || s.model,
      scheme: (ttsScheme || s.scheme) === 'whole' ? 'whole' : 'turns',
      voices: `${s.voice1} / ${s.voice2}`,
      temperature: s.temperature,
      settings: s,
    };
  }, [ttsPreset, ttsScheme, ttsModel, showScope]);
  // What these settings send, asked of the server on a two-turn sample — the
  // same planning a take goes through (prompt or per-turn styles, the
  // scheme), so the panel can't show a shape the batch doesn't send.
  const samplePayload = useTtsPreview(showScope && questId
    ? buildTtsRequest({ text: SAMPLE_TAKE, settings: { ...ttsPreview.settings, model: ttsPreview.model, scheme: ttsPreview.scheme }, questId })
    : null);
  let sampleText = 'Loading…';
  if (samplePayload.error) sampleText = `Could not get what will be sent: ${samplePayload.error}`;
  else if (samplePayload.data) sampleText = samplePayload.data.requests.map((r) => requestPreviewText(samplePayload.data, r)).join('\n\n');

  const points = (locations || []).filter((l) => l.uuid && pointsByUuid && pointsByUuid[l.uuid]);
  // Point numbers as shown in the editor list — every point list here names
  // a point as «N. Name», not by name alone.
  const orderByUuid = useMemo(() => {
    const m = {};
    (locations || []).forEach((l, i) => { if (l.uuid) m[l.uuid] = i + 1; });
    return m;
  }, [locations]);
  // Default scope: a human said the text is ready for voice-over and the
  // point hasn't been voiced by this pipeline yet.
  const readyUuids = points.filter((l) => isReady(l) && !isVoiced(l) && hasAnyText(l)).map((l) => l.uuid);
  const noVoTextUuids = points.filter((l) => fieldsNoVoText(l).length > 0).map((l) => l.uuid);
  const notVoicedUuids = points.filter((l) => !isVoiced(l) && hasAnyText(l)).map((l) => l.uuid);
  const allUuids = points.filter(hasAnyText).map((l) => l.uuid);

  const selectedUuids = points.filter((l) => selected[l.uuid]).map((l) => l.uuid);
  const selectedCount = selectedUuids.length;
  const sameSet = (arr) => arr.length === selectedCount && arr.every((u) => selected[u]);
  const activePreset = sameSet(readyUuids) ? 'ready'
    : sameSet(noVoTextUuids) ? 'novotext'
    : sameSet(notVoicedUuids) ? 'notvoiced'
    : sameSet(allUuids) ? 'all' : 'custom';
  const setPreset = (uuids) => setSelected(Object.fromEntries(uuids.map((u) => [u, true])));

  useEffect(() => {
    if ((!active && !yoActive && !glActive) || !runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'voiceover_batch') {
        setBatch(msg.batch);
      } else if (msg.type === 'voiceover_batch_done') {
        setBatch(msg.batch);
        setActive(false);
      } else if (msg.type === 'yofication_batch') {
        setYoBatch(msg.batch);
      } else if (msg.type === 'yofication_batch_done') {
        setYoBatch(msg.batch);
        setYoActive(false);
      } else if (msg.type === 'glossary_batch') {
        setGlBatch(msg.batch);
      } else if (msg.type === 'glossary_batch_done') {
        setGlBatch(msg.batch);
        setGlActive(false);
        fetchGlossaryCount();
      }
      // Voiceover texts, tags and comments land in the editor live via the
      // server's per-field events — no quest refetch needed here.
    };
    es.onerror = () => { /* auto-reconnects; ignore */ };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, yoActive, glActive, runId]);

  const openScope = () => {
    setError(null);
    setPreset(readyUuids);
    setShowScope(true);
    // Probe the Gemini keys right away — dead keys should be visible BEFORE
    // the user starts a long batch, not after it fails.
    setKeyCheck([{ label: 'checking keys…', keyMasked: '', ok: null }]);
    papi('/gemini-keys/check', {})
      .then((r) => setKeyCheck(r))
      .catch((e) => setKeyCheck([{ label: 'key check failed', keyMasked: e.message, ok: false }]));
  };

  const start = async (body) => {
    setError(null); setBusy(true);
    try {
      const r = await papi(`/pipeline/quest/${questId}/voiceover-batch`, body);
      clearedRef.current = false;
      setBatch({ status: 'running', total: r.total, done_count: 0, voiced_count: 0, error_count: 0, points: [], eta_ms: null, keys: [] });
      setActive(true);
      setShowScope(false);
      setCollapsed(false);
      // One-off instructions don't carry over to the next batch.
      setVoiceSplitExtra('');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const startBatch = () => {
    if (!selectedUuids.length) { setError('Select at least one point'); return; }
    start({ uuids: selectedUuids, skipVoiced, tts: currentTtsSettings(ttsPreset, ttsScheme, ttsModel), voiceSplitExtra });
  };

  const stopBatch = async () => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/voiceover-batch/stop`, {}); } catch (e) { setError(e.message); }
  };

  const runUnfinished = () => {
    const uuids = (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).map((bp) => bp.uuid);
    if (!uuids.length) return;
    start({ uuids, skipVoiced, tts: currentTtsSettings(ttsPreset, ttsScheme, ttsModel), voiceSplitExtra });
  };

  // --- Prep actions ---------------------------------------------------------

  const openYoScope = () => {
    setError(null);
    // Default scope: every point — the "whole quest" pass; narrow it for
    // re-runs after editing a couple of points.
    setYoSelected(Object.fromEntries(points.map((l) => [l.uuid, true])));
    setShowYoScope(true);
  };

  const startYofication = async () => {
    const uuids = points.filter((l) => yoSelected[l.uuid]).map((l) => l.uuid);
    if (!uuids.length) { setError('Select at least one point'); return; }
    setError(null); setBusy(true);
    try {
      const r = await papi(`/pipeline/quest/${questId}/yofication-batch`, { uuids });
      yoClearedRef.current = false;
      setYoBatch({ status: 'running', total: r.total, done_count: 0, error_count: 0, points: [] });
      setYoActive(true);
      setShowYoScope(false);
      setCollapsed(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const startGlossaryBatch = async () => {
    if (!window.confirm('Collect doubtful words from all points and web-verify the new ones? New entries are APPENDED to the glossary; existing entries are never changed. Run ё-fication first if the texts still need it.')) return;
    setError(null); setBusy(true);
    try {
      const r = await papi(`/pipeline/quest/${questId}/glossary-batch`, {});
      glClearedRef.current = false;
      setGlBatch({ status: 'running', phase: 'collect', total: r.total, done_count: 0, error_count: 0, points: [], verify: null });
      setGlActive(true);
      setCollapsed(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const stopPrep = (kind) => async () => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/${kind}/stop`, {}); } catch (e) { setError(e.message); }
  };
  const clearPrep = (kind, setB, clearedRef) => async () => {
    setError(null);
    try {
      await papi(`/pipeline/quest/${questId}/${kind}/clear`, {});
      clearedRef.current = true;
      setB(null);
    } catch (e) { setError(e.message); }
  };

  const clearBatch = async () => {
    if (!window.confirm('Delete this voiceover-batch record? Written texts and audio versions are kept.')) return;
    setError(null); setBusy(true);
    try {
      await papi(`/pipeline/quest/${questId}/voiceover-batch/clear`, {});
      clearedRef.current = true;
      setBatch(null);
      setActive(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const running = active && batch && batch.status === 'running';
  const yoRunning = yoActive && yoBatch && yoBatch.status === 'running';
  const glRunning = glActive && glBatch && glBatch.status === 'running';
  const anyRunning = running || yoRunning || glRunning;
  const done = batch && batch.done_count != null ? batch.done_count : 0;
  const total = batch && batch.total != null ? batch.total : 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const unfinishedCount = batch ? (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).length : 0;

  // Report status to the tab shell (badge + running dot) when embedded. The
  // dot pulses for prep batches too — they hold the same run slot.
  useEffect(() => {
    if (onStatus) onStatus({ running: anyRunning, done, total, status: batch ? batch.status : null, hasBatch: !!(batch || yoBatch || glBatch) });
  }, [anyRunning, done, total, batch, yoBatch, glBatch, onStatus]);

  // In the shared tabbed shell the outer frame/collapse belong to the shell.
  const effCollapsed = embedded ? false : collapsed;
  const box = embedded
    ? { padding: '4px 2px 2px' }
    : {
        border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px',
        margin: '8px 8px 0', background: 'rgba(142,68,173,0.04)',
      };
  const primaryBtn = { background: '#8e44ad', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };

  return (
    <div style={box}>
      <div
        onClick={embedded ? undefined : () => setCollapsed((c) => !c)}
        title={embedded ? undefined : (collapsed ? 'Expand' : 'Collapse')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: embedded ? 'default' : 'pointer' }}
      >
        {!embedded && <span style={{ fontWeight: 700, color: '#8e44ad' }}>🔊 Voiceover</span>}
        {!embedded && collapsed && batch && (
          <span style={{ fontSize: 12, color: '#5f6368' }}>
            {batch.done_count || 0}/{batch.total || 0}
            {!running && batch.status ? ` · ${batch.status}` : ''}
          </span>
        )}
        {!running && (
          <button style={primaryBtn} disabled={busy || !points.length} onClick={(e) => { e.stopPropagation(); openScope(); }}>
            Voice points…{readyUuids.length ? ` (${readyUuids.length} ready)` : ''}
          </button>
        )}
        {running && <button style={stopBtn} onClick={(e) => { e.stopPropagation(); stopBatch(); }}>■ Stop (running points finish)</button>}
        {!running && batch && unfinishedCount > 0 && (
          <button onClick={(e) => { e.stopPropagation(); runUnfinished(); }} disabled={busy} style={{ padding: '6px 12px', borderRadius: 4 }}>
            ↻ Run unfinished ({unfinishedCount})
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {running && <span style={{ fontSize: 12, color: '#5f6368' }}>ETA {fmtEta(batch.eta_ms)}</span>}
          {!running && batch && (
            <button
              className="remove-location-button"
              onClick={(e) => { e.stopPropagation(); clearBatch(); }}
              disabled={busy}
              title="Delete this voiceover-batch record — written texts and audio versions are kept"
              aria-label="Delete voiceover batch record"
            >×</button>
          )}
          {!embedded && <span style={{ color: '#8e44ad', fontSize: 13 }}>{collapsed ? '▸' : '▾'}</span>}
        </span>
      </div>

      {error && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {/* Prep: quest-level actions that come BEFORE voicing — ё-fication of
          the source texts and the stress glossary (collect + edit). */}
      {!effCollapsed && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: '#5f6368' }}>Prep:</span>
            <button
              onClick={openYoScope}
              disabled={busy || anyRunning || !points.length}
              style={{ padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}
              title="Model pass per point: restores «ё» in the source description / task / after_message (needs sentence context). Edits the readable originals; run it before collecting the glossary. Opens a point picker."
            >Ё-fy texts…</button>
            <button
              onClick={startGlossaryBatch}
              disabled={busy || anyRunning || !points.length}
              style={{ padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}
              title="Collect doubtful word forms from every point (no web), then ONE web-checked verify pass over the deduped list. New entries are appended to the glossary; existing entries are never touched."
            >Collect glossary</button>
            <button
              onClick={() => setShowGlossary(true)}
              style={{ padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}
              title="The per-quest stress glossary — the rule every voiceover text is read by: each place without a stress mark of its own gets the glossary's"
            >📖 Glossary{glossaryCount != null ? ` (${glossaryCount})` : ''}</button>
            <button
              onClick={() => setShowReplacements(true)}
              style={{ padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}
              title="Regex replacements run on every text on its way to TTS, before the glossary — e.g. a name the model misreads"
            >🔁 Replacements</button>
            <ModelTagRow label="Runs on:" keys={['yofication', 'glossary']} />
          </div>
          <PrepBatchBlock
            title="Ё-fication"
            batch={yoBatch}
            running={yoRunning}
            onStop={stopPrep('yofication-batch')}
            onClear={clearPrep('yofication-batch', setYoBatch, yoClearedRef)}
            orderByUuid={orderByUuid}
            rightInfo={(bp) => (bp.changes != null ? `${bp.changes} ё` : '')}
            footer={yoBatch && !yoRunning && yoBatch.changed_count != null ? (
              <div style={{ fontSize: 11, color: '#5f6368', marginTop: 4 }}>
                {yoBatch.changed_count} letter(s) changed across the quest.
              </div>
            ) : null}
          />
          <PrepBatchBlock
            title="Glossary"
            batch={glBatch}
            progress={glossaryProgress}
            running={glRunning}
            onStop={stopPrep('glossary-batch')}
            onClear={clearPrep('glossary-batch', setGlBatch, glClearedRef)}
            orderByUuid={orderByUuid}
            rightInfo={(bp) => (bp.candidates != null ? `${bp.candidates} word(s)` : '')}
            footer={glBatch && glBatch.verify ? (
              <div style={{ fontSize: 11, color: '#5f6368', marginTop: 4 }}>
                {glBatch.verify.stored} new mark(s), {glBatch.verify.no_mark} no-mark
                {' · already known '}{glBatch.verify.dropped_known}
                {', not in texts '}{glBatch.verify.dropped_ghost}
                {', unverified '}{glBatch.verify.unverified}
                {glBatch.verify.errors && glBatch.verify.errors.length > 0 && (
                  <span title={glBatch.verify.errors.join('\n')} style={{ color: '#c5221f', cursor: 'help' }}> ⚠ {glBatch.verify.errors.length} problem(s)</span>
                )}
              </div>
            ) : null}
          />
        </div>
      )}

      {!effCollapsed && batch && (batch.points || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: running ? '#8e44ad' : '#188038', transition: 'width .3s' }} />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>
              {done} / {total}
              {batch.error_count ? ` · ${batch.error_count} failed` : ''}
              {!running && batch.status ? ` · ${batch.status}` : ''}
            </span>
          </div>
          {batch.keys && batch.keys.length > 0 && (
            <div style={{ marginBottom: 6 }}><KeysStatus keys={batch.keys} /></div>
          )}
          <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 4 }}>
            {(batch.points || []).map((bp) => (
              <PointRow key={bp.uuid} bp={bp} num={orderByUuid[bp.uuid]} />
            ))}
          </div>
        </div>
      )}

      {showScope && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} {...backdropClose(() => setShowScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 880, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Voice points</h3>
            <div style={{ marginBottom: 8 }}>
              <ModelTagRow label="Runs on:" keys={['voicesplit']} />
              {ttsPreview && ttsPreview.model && (
                <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 6 }}>· TTS <b>{ttsPreview.model}</b></span>
              )}
              <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 6 }}>· stress marks: 📖 glossary (code{glossaryCount != null ? `, ${glossaryCount} entries` : ''})</span>
            </div>
            <div style={{ marginBottom: 8 }}><KeysStatus keys={keyCheck} /></div>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Quick select:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { key: 'ready', label: `Tagged «${READY_TAG}», not voiced yet (${readyUuids.length})`, uuids: readyUuids },
                { key: 'novotext', label: `✎ No voiceover text (${noVoTextUuids.length})`, uuids: noVoTextUuids },
                { key: 'notvoiced', label: `Not voiced (${notVoicedUuids.length})`, uuids: notVoicedUuids },
                { key: 'all', label: `All with text (${allUuids.length})`, uuids: allUuids },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.uuids)}
                  style={{
                    padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12,
                    border: activePreset === p.key ? '2px solid #8e44ad' : '1px solid var(--border,#ccc)',
                    background: activePreset === p.key ? 'rgba(142,68,173,0.10)' : 'transparent',
                    fontWeight: activePreset === p.key ? 600 : 400,
                  }}
                >{p.label}</button>
              ))}
              <button
                onClick={() => setSelected({})}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent', marginLeft: 'auto' }}
              >Clear</button>
            </div>

            {/* Two columns: WHICH points on the left, HOW to voice on the right. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>

              {/* LEFT — point selection */}
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Points</div>
                <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
                  {points.map((l) => {
                    const ready = isReady(l);
                    const voiced = isVoiced(l);
                    const empty = !hasAnyText(l);
                    const noVoText = fieldsNoVoText(l);
                    return (
                      <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: 'pointer', opacity: empty ? 0.5 : 1 }}>
                        <input type="checkbox" checked={!!selected[l.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderByUuid[l.uuid]}. {l.name}</span>
                        {ready && (
                          <span
                            style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }}
                            title={`Has the manual «${READY_TAG}» tag — a human marked this point's text as approved for voicing (set via the tag chips on the point)`}
                          >🎙️ ready tag</span>
                        )}
                        {voiced && (
                          <span
                            style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }}
                            title={`Has the «${VOICED_TAG}» tag — this pipeline already voiced the point successfully`}
                          >🔊 voiced</span>
                        )}
                        {empty
                          ? <span style={{ fontSize: 10, opacity: 0.5 }}>no text</span>
                          : noVoText.length
                            ? <span style={{ fontSize: 10, color: '#9a6700', whiteSpace: 'nowrap' }} title={`No voiceover text yet for: ${noVoText.join(', ')} — the batch will build it from the source + glossary`}>
                                ✎ no VO text: {noVoText.map((f) => FIELD_SHORT[f] || f).join(', ')}
                              </span>
                            : <span style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }} title="Every texted field has its voiceover text">✓ VO text</span>}
                      </label>
                    );
                  })}
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={skipVoiced} onChange={(e) => setSkipVoiced(e.target.checked)} />
                  Skip fields that already have an audio version (re-runs only voice what's missing, failed, or whose text changed)
                </label>

                <div style={{ fontSize: 12, marginTop: 8 }}>
                  <strong>{selectedCount}</strong> point(s) will be voiced
                  {activePreset !== 'custom' ? '' : ' (custom)'}
                </div>
              </div>

              {/* RIGHT — how to voice */}
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Voiceover settings</div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ minWidth: 54 }}>Preset</span>
                  <select value={ttsPreset} onChange={(e) => setTtsPreset(e.target.value)} style={{ flex: 1, padding: 4, fontSize: 12 }}>
                    <option value="">Last used (default)</option>
                    {presetNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
                  title="The TTS model for this run. The request shape follows the model: up to 3.1 the director's prompt and the labeled transcript go as one text; from 3.8 each turn is a text part with its speaker and style.">
                  <span style={{ minWidth: 54 }}>Model</span>
                  <select value={ttsModel} onChange={(e) => setTtsModel(e.target.value)} style={{ flex: 1, padding: 4, fontSize: 12 }}>
                    <option value="">As in the settings</option>
                    {TTS_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
                  title="How each take goes to Google. Per turn: one request per Speaker turn, that voice in both slots, glued here — what 3.1 needed to keep the voices apart. Whole: the take as one request with both voices, one quota unit per take.">
                  <span style={{ minWidth: 54 }}>Scheme</span>
                  <select value={ttsScheme} onChange={(e) => setTtsScheme(e.target.value)} style={{ flex: 1, padding: 4, fontSize: 12 }}>
                    <option value="">As in the settings</option>
                    {TTS_SCHEMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>

                {/* Prompt preview — what this preset actually sends to TTS. */}
                <div style={{ border: '1px solid var(--border,#eee)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ padding: '4px 8px', background: 'rgba(0,0,0,0.03)', display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: '#555' }}>
                    <span>model <b>{ttsPreview.model}</b></span>
                    <span title={schemeLabel(ttsPreview.scheme)}>scheme <b>{ttsPreview.scheme === 'whole' ? 'whole take' : 'per turn'}</b></span>
                    <span>voices <b>{ttsPreview.voices}</b></span>
                    <span>temp <b>{ttsPreview.temperature}</b></span>
                  </div>
                  <div style={{ padding: '3px 8px', fontSize: 11, color: '#888', borderTop: '1px solid var(--border,#eee)' }}>
                    Planned on a two-turn sample, not on a point's text — the shape every take of this run gets:
                  </div>
                  <pre style={{ margin: 0, maxHeight: 150, overflow: 'auto', padding: '6px 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, lineHeight: 1.35 }}>{sampleText}</pre>
                </div>

                <label style={{ display: 'block', fontSize: 12 }}>
                  Extra instruction — role splitter <span style={{ color: '#888' }}>(appended; this run only)</span>
                  <textarea value={voiceSplitExtra} onChange={(e) => setVoiceSplitExtra(e.target.value)} rows={3}
                    placeholder="e.g. keep the opening paragraph on Speaker 1"
                    style={{ width: '100%', marginTop: 2, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} />
                </label>
              </div>
            </div>

            {/* Footer — full width */}
            <div style={{ fontSize: 12, color: '#9a6700', background: 'rgba(154,103,0,0.08)', borderRadius: 6, padding: '6px 8px', marginTop: 12 }}>
              «{READY_TAG}» is the manual tag a human puts on a point (via its tag chips) once the text is approved for voicing — the default selection here.
              Per point: the text read through the 📖 glossary (places with a mark of their own keep it) → voice split (Speaker 1/2 per paragraph) → TTS with the {ttsPreset ? `«${ttsPreset}» preset` : 'last-used Create Voiceover settings'} → becomes the active audio version → tag «{VOICED_TAG}». Points run in parallel; expect a few minutes per point.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowScope(false)}>Cancel</button>
              <button style={primaryBtn} disabled={busy || selectedCount === 0} onClick={startBatch}>Start ({selectedCount})</button>
            </div>
          </div>
        </div>
      )}

      {showYoScope && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} {...backdropClose(() => setShowYoScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 480, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Ё-fy source texts</h3>
            <div style={{ fontSize: 12, color: '#9a6700', background: 'rgba(154,103,0,0.08)', borderRadius: 6, padding: '6px 8px', marginBottom: 10 }}>
              Restores «ё» in the SOURCE description / task / after_message of the selected
              points — this edits the readable originals in place (validated char-by-char,
              only е→ё can change). Default: the whole quest; narrow it when only a couple
              of points were edited.
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button
                onClick={() => setYoSelected(Object.fromEntries(points.map((l) => [l.uuid, true])))}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent' }}
              >All ({points.length})</button>
              <button
                onClick={() => setYoSelected({})}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent' }}
              >Clear</button>
            </div>
            <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
              {points.map((l) => (
                <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!yoSelected[l.uuid]} onChange={(e) => setYoSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderByUuid[l.uuid]}. {l.name}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#5f6368', marginRight: 'auto' }}>
                {points.filter((l) => yoSelected[l.uuid]).length} point(s) selected
              </span>
              <button onClick={() => setShowYoScope(false)}>Cancel</button>
              <button
                style={primaryBtn}
                disabled={busy || points.filter((l) => yoSelected[l.uuid]).length === 0}
                onClick={startYofication}
              >Start</button>
            </div>
          </div>
        </div>
      )}

      {showReplacements && (
        <ReplacementsEditor questId={questId} onClose={() => setShowReplacements(false)} />
      )}

      {showGlossary && (
        <GlossaryEditor
          questId={questId}
          locations={locations}
          onClose={() => setShowGlossary(false)}
          onSaved={(g) => setGlossaryCount(g.length)}
        />
      )}
    </div>
  );
};

export default VoiceoverBatchPanel;